import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FiMic, FiMicOff, FiChevronDown } from 'react-icons/fi';
import styled from 'styled-components';
import {
  SUPPORTED_LANGUAGES,
  SAFARI_SUPPORTED_LANGUAGES,
  isSafari
} from '../../hooks/useSpeechToText';
import Tooltip from '../Tooltip';
import type { RootState } from '../../../../state/store';
import { useSelector } from 'react-redux';

interface VoiceInputButtonProps {
  currentLanguage: string;
  onLanguageChange: (language: string) => void;
  isListening: boolean;
  onMicrophoneClick: () => void;
  theme: any;
  disabled?: boolean;
  isSupported?: boolean;
}

const VoiceInputButton: React.FC<VoiceInputButtonProps> = ({
  currentLanguage,
  onLanguageChange,
  isListening,
  onMicrophoneClick,
  theme,
  disabled = false,
  isSupported = true
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const themeMode = useSelector((state: RootState) => state.theme?.mode || 'light');

  // Detect if browser is Safari
  const isSafariBrowser = useMemo(() => isSafari(), []);

  // All languages grouped by country
  const allLanguagesByCountry: Record<string, string[]> = {
    '🇺🇸 United States': ['en-US'],
    '🇬🇧 United Kingdom': ['en-GB'],
    '🇮🇳 India': [
      'en-IN',
      'hi-IN',
      'bn-IN',
      'ta-IN',
      'te-IN',
      'mr-IN',
      'gu-IN',
      'kn-IN',
      'ml-IN',
      'pa-IN',
      'or-IN',
      'as-IN',
      'ur-IN'
    ],
    '🇫🇷 France': ['fr-FR'],
    '🇪🇸 Spain': ['es-ES', 'eu-ES', 'ca-ES', 'gl-ES'],
    '🇩🇪 Germany': ['de-DE'],
    '🇮🇹 Italy': ['it-IT'],
    '🇧🇷 Brazil': ['pt-BR'],
    '🇷🇺 Russia': ['ru-RU'],
    '🇨🇳 China': ['zh-CN'],
    '🇹🇼 Taiwan': ['zh-TW'],
    '🇭🇰 Hong Kong': ['zh-HK'],
    '🇯🇵 Japan': ['ja-JP'],
    '🇰🇷 South Korea': ['ko-KR'],
    '🇹🇭 Thailand': ['th-TH'],
    '🇻🇳 Vietnam': ['vi-VN'],
    '🇮🇩 Indonesia': ['id-ID'],
    '🇲🇾 Malaysia': ['ms-MY'],
    '🇵🇭 Philippines': ['tl-PH'],
    '🇧🇩 Bangladesh': ['bn-BD'],
    '🇱🇰 Sri Lanka': ['ta-LK', 'si-LK'],
    '🇳🇵 Nepal': ['ne-NP'],
    '🇵🇰 Pakistan': ['ur-PK'],
    '🇸🇦 Saudi Arabia': ['ar-SA'],
    '🇪🇬 Egypt': ['ar-EG'],
    '🇦🇪 UAE': ['ar-AE'],
    '🇯🇴 Jordan': ['ar-JO'],
    '🇱🇧 Lebanon': ['ar-LB'],
    '🇲🇦 Morocco': ['ar-MA'],
    '🇹🇳 Tunisia': ['ar-TN'],
    '🇩🇿 Algeria': ['ar-DZ'],
    '🇮🇶 Iraq': ['ar-IQ'],
    '🇰🇼 Kuwait': ['ar-KW'],
    '🇱🇾 Libya': ['ar-LY'],
    '🇶🇦 Qatar': ['ar-QA'],
    '🇸🇾 Syria': ['ar-SY'],
    '🇾🇪 Yemen': ['ar-YE'],
    '🇳🇱 Netherlands': ['nl-NL'],
    '🇸🇪 Sweden': ['sv-SE'],
    '🇩🇰 Denmark': ['da-DK'],
    '🇳🇴 Norway': ['no-NO'],
    '🇫🇮 Finland': ['fi-FI'],
    '🇵🇱 Poland': ['pl-PL'],
    '🇨🇿 Czech Republic': ['cs-CZ'],
    '🇸🇰 Slovakia': ['sk-SK'],
    '🇭🇺 Hungary': ['hu-HU'],
    '🇷🇴 Romania': ['ro-RO'],
    '🇧🇬 Bulgaria': ['bg-BG'],
    '🇭🇷 Croatia': ['hr-HR'],
    '🇷🇸 Serbia': ['sr-RS'],
    '🇸🇮 Slovenia': ['sl-SI'],
    '🇪🇪 Estonia': ['et-EE'],
    '🇱🇻 Latvia': ['lv-LV'],
    '🇱🇹 Lithuania': ['lt-LT'],
    '🇬🇷 Greece': ['el-GR'],
    '🇮🇷 Iran': ['fa-IR'],
    '🇮🇱 Israel': ['he-IL'],
    '🇹🇷 Turkey': ['tr-TR'],
    '🇦🇿 Azerbaijan': ['az-AZ'],
    '🇬🇪 Georgia': ['ka-GE'],
    '🇦🇲 Armenia': ['hy-AM'],
    '🇲🇹 Malta': ['mt-MT'],
    '🇮🇸 Iceland': ['is-IS'],
    '🏴󠁧󠁢󠁷󠁬󠁳󠁿 Wales': ['cy-GB'],
    '🇮🇪 Ireland': ['ga-IE'],
    '🇿🇦 South Africa': ['af-ZA'],
    '🇰🇪 Kenya': ['sw-KE'],
    '🇪🇹 Ethiopia': ['am-ET']
  };

  // Safari only supports English - show limited language options
  const safariLanguagesByCountry: Record<string, string[]> = {
    '🇺🇸 United States': ['en-US'],
    '🇬🇧 United Kingdom': ['en-GB'],
    '🇮🇳 India': ['en-IN']
  };

  // Use appropriate language list based on browser
  const languagesByCountry = isSafariBrowser ? safariLanguagesByCountry : allLanguagesByCountry;

  // Get the appropriate SUPPORTED_LANGUAGES based on browser
  const availableLanguages = isSafariBrowser ? SAFARI_SUPPORTED_LANGUAGES : SUPPORTED_LANGUAGES;

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleDropdownToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!disabled && !isListening) {
      setIsDropdownOpen(!isDropdownOpen);
    }
  };

  const handleMicrophoneClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onMicrophoneClick();
  };

  const handleLanguageSelect = (langCode: string) => {
    onLanguageChange(langCode);
    setIsDropdownOpen(false);
  };

  const getShortLanguageName = (langCode: string): string => {
    const shortNames: { [key: string]: string } = {
      'en-US': 'EN',
      'en-GB': 'EN',
      'en-IN': 'EN',
      'hi-IN': 'हिं',
      'bn-IN': 'বাং',
      'bn-BD': 'বাং',
      'ta-IN': 'தமி',
      'ta-LK': 'தমি',
      'te-IN': 'తెలు',
      'mr-IN': 'मर',
      'gu-IN': 'ગુજ',
      'kn-IN': 'ಕನ್ನ',
      'ml-IN': 'മല',
      'pa-IN': 'ਪੰਜ',
      'or-IN': 'ଓଡି',
      'as-IN': 'অসম',
      'ne-NP': 'नेप',
      'si-LK': 'සිං',
      'ur-IN': 'اردو',
      'ur-PK': 'اردو',
      'ar-SA': 'AR',
      'ar-EG': 'AR',
      'ar-AE': 'AR',
      'ar-JO': 'AR',
      'ar-LB': 'AR',
      'ar-MA': 'AR',
      'ar-TN': 'AR',
      'ar-DZ': 'AR',
      'ar-IQ': 'AR',
      'ar-KW': 'AR',
      'ar-LY': 'AR',
      'ar-QA': 'AR',
      'ar-SY': 'AR',
      'ar-YE': 'AR',
      'zh-CN': 'ZH',
      'zh-TW': 'ZH',
      'zh-HK': 'ZH',
      'ja-JP': 'JA',
      'ko-KR': 'KO',
      'th-TH': 'TH',
      'vi-VN': 'VI',
      'id-ID': 'ID',
      'ms-MY': 'MS',
      'tl-PH': 'TL',
      'fr-FR': 'FR',
      'es-ES': 'ES',
      'de-DE': 'DE',
      'it-IT': 'IT',
      'pt-BR': 'PT',
      'ru-RU': 'RU',
      'nl-NL': 'NL',
      'sv-SE': 'SV',
      'da-DK': 'DA',
      'no-NO': 'NO',
      'fi-FI': 'FI',
      'pl-PL': 'PL',
      'cs-CZ': 'CS',
      'sk-SK': 'SK',
      'hu-HU': 'HU',
      'ro-RO': 'RO',
      'bg-BG': 'BG',
      'hr-HR': 'HR',
      'sr-RS': 'SR',
      'sl-SI': 'SL',
      'et-EE': 'ET',
      'lv-LV': 'LV',
      'lt-LT': 'LT',
      'el-GR': 'EL',
      'fa-IR': 'فا',
      'he-IL': 'עב',
      'tr-TR': 'TR',
      'az-AZ': 'AZ',
      'ka-GE': 'KA',
      'hy-AM': 'HY',
      'eu-ES': 'EU',
      'ca-ES': 'CA',
      'gl-ES': 'GL',
      'mt-MT': 'MT',
      'is-IS': 'IS',
      'cy-GB': 'CY',
      'ga-IE': 'GA',
      'af-ZA': 'AF',
      'sw-KE': 'SW',
      'am-ET': 'AM'
    };
    return shortNames[langCode] || langCode.split('-')[0].toUpperCase();
  };

  const getLanguageFlag = (langCode: string): string => {
    const flagMap: { [key: string]: string } = {
      'en-US': '🇺🇸',
      'en-GB': '🇬🇧',
      'en-IN': '🇮🇳',
      'hi-IN': '🇮🇳',
      'bn-IN': '🇮🇳',
      'bn-BD': '🇧🇩',
      'ta-IN': '🇮🇳',
      'ta-LK': '🇱🇰',
      'te-IN': '🇮🇳',
      'mr-IN': '🇮🇳',
      'gu-IN': '🇮🇳',
      'kn-IN': '🇮🇳',
      'ml-IN': '🇮🇳',
      'pa-IN': '🇮🇳',
      'or-IN': '🇮🇳',
      'as-IN': '🇮🇳',
      'ne-NP': '🇳🇵',
      'si-LK': '🇱🇰',
      'ur-IN': '🇮🇳',
      'ur-PK': '🇵🇰',
      'ar-SA': '🇸🇦',
      'ar-EG': '🇪🇬',
      'ar-AE': '🇦🇪',
      'ar-JO': '🇯🇴',
      'ar-LB': '🇱🇧',
      'ar-MA': '🇲🇦',
      'ar-TN': '🇹🇳',
      'ar-DZ': '🇩🇿',
      'ar-IQ': '🇮🇶',
      'ar-KW': '🇰🇼',
      'ar-LY': '🇱🇾',
      'ar-QA': '🇶🇦',
      'ar-SY': '🇸🇾',
      'ar-YE': '🇾🇪',
      'zh-CN': '🇨🇳',
      'zh-TW': '🇹🇼',
      'zh-HK': '🇭🇰',
      'ja-JP': '🇯🇵',
      'ko-KR': '🇰🇷',
      'th-TH': '🇹🇭',
      'vi-VN': '🇻🇳',
      'id-ID': '🇮🇩',
      'ms-MY': '🇲🇾',
      'tl-PH': '🇵🇭',
      'fr-FR': '🇫🇷',
      'es-ES': '🇪🇸',
      'de-DE': '🇩🇪',
      'it-IT': '🇮🇹',
      'pt-BR': '🇧🇷',
      'ru-RU': '🇷🇺',
      'nl-NL': '🇳🇱',
      'sv-SE': '🇸🇪',
      'da-DK': '🇩🇰',
      'no-NO': '🇳🇴',
      'fi-FI': '🇫🇮',
      'pl-PL': '🇵🇱',
      'cs-CZ': '🇨🇿',
      'sk-SK': '🇸🇰',
      'hu-HU': '🇭🇺',
      'ro-RO': '🇷🇴',
      'bg-BG': '🇧🇬',
      'hr-HR': '🇭🇷',
      'sr-RS': '🇷🇸',
      'sl-SI': '🇸🇮',
      'et-EE': '🇪🇪',
      'lv-LV': '🇱🇻',
      'lt-LT': '🇱🇹',
      'el-GR': '🇬🇷',
      'fa-IR': '🇮🇷',
      'he-IL': '🇮🇱',
      'tr-TR': '🇹🇷',
      'az-AZ': '🇦🇿',
      'ka-GE': '🇬🇪',
      'hy-AM': '🇦🇲',
      'eu-ES': '🇪🇸',
      'ca-ES': '🇪🇸',
      'gl-ES': '🇪🇸',
      'mt-MT': '🇲🇹',
      'is-IS': '🇮🇸',
      'cy-GB': '🏴󠁧󠁢󠁷󠁬󠁳󠁿',
      'ga-IE': '🇮🇪',
      'af-ZA': '🇿🇦',
      'sw-KE': '🇰🇪',
      'am-ET': '🇪🇹'
    };
    return flagMap[langCode] || '🌐';
  };

  const getTooltipContent = () => {
    if (!isSupported) {
      return 'Voice input is not supported in this browser. Please use Google Chrome or Safari';
    }
    return isListening ? 'Stop recording' : 'Voice input';
  };

  return (
    <VoiceInputWrapper ref={dropdownRef} $theme={theme}>
      <Tooltip content={getTooltipContent()} position="bottom">
        <MicButton
          type="button"
          onClick={handleMicrophoneClick}
          $theme={theme}
          disabled={disabled || !isSupported}
          isListening={isListening}
          data-listening={isListening}
          aria-label={isListening ? 'Stop recording' : 'Start voice input'}
        >
          {isListening ? <FiMicOff size={20} /> : <FiMic size={20} />}
          <LanguageIndicator $theme={theme}>
            {getShortLanguageName(currentLanguage)}
          </LanguageIndicator>
        </MicButton>
      </Tooltip>

      <Tooltip
        content={
          !isSupported
            ? 'Voice input is not supported in this browser. Please use Google Chrome or Safari'
            : 'Language selection'
        }
        position="bottom"
      >
        <DropdownArrowButton
          type="button"
          onClick={handleDropdownToggle}
          $theme={theme}
          disabled={disabled || isListening || !isSupported}
          isOpen={isDropdownOpen}
          aria-label="Select language"
        >
          <FiChevronDown size={12} />
        </DropdownArrowButton>
      </Tooltip>

      {isDropdownOpen && (
        <DropdownMenu $theme={theme}>
          <LanguagesList>
            {Object.entries(languagesByCountry).map(([country, languages]) => (
              <CountryGroup key={country}>
                <CountryHeader $theme={theme} themeMode={themeMode}>
                  {country}
                </CountryHeader>
                <LanguagesGroup>
                  {languages.map((langCode: string) => (
                    <LanguageOption
                      key={langCode}
                      onClick={() => handleLanguageSelect(langCode)}
                      $theme={theme}
                      isSelected={langCode === currentLanguage}
                    >
                      <LanguageInfo>
                        <LanguageFlag>{getLanguageFlag(langCode)}</LanguageFlag>
                        <LanguageName $theme={theme}>
                          {availableLanguages[langCode as keyof typeof availableLanguages]}
                        </LanguageName>
                      </LanguageInfo>
                      <LanguageCode $theme={theme}>{langCode}</LanguageCode>
                    </LanguageOption>
                  ))}
                </LanguagesGroup>
              </CountryGroup>
            ))}
          </LanguagesList>
        </DropdownMenu>
      )}
    </VoiceInputWrapper>
  );
};

const VoiceInputWrapper = styled.div<{ $theme: any }>`
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const MicButton = styled.button<{
  $theme: any;
  disabled: boolean;
  isListening: boolean;
}>`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: #3c97d8;
  border-radius: 50%;
  border: none;
  color: white;
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
  opacity: ${(props) => (props.disabled ? 0.5 : 1)};
  transition: all 200ms ease;
  &:hover:not(:disabled) {
    background: #3c97d8;
    transform: scale(1.05);
  }

  &:active:not(:disabled) {
    transform: scale(0.95);
  }

  ${(props) =>
    props.isListening &&
    `
    animation: pulse 1.5s infinite;
    background: #dc3545;
    
    &:hover:not(:disabled) {
      background: #c82333;
    }
    
    @keyframes pulse {
      0% {
        transform: scale(1);
        opacity: 1;
      }
      50% {
        transform: scale(1.05);
        opacity: 0.8;
      }
      100% {
        transform: scale(1);
        opacity: 1;
      }
    }
  `}

  @media (max-width: 768px) {
    width: 36px;
    height: 36px;
  }
`;

const LanguageIndicator = styled.div<{ $theme: any }>`
  position: absolute;
  top: -2px;
  right: -2px;
  width: 18px;
  height: 18px;
  background: #2d2d2d;
  border: 1px solid #2d2d2d;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px !important;
  font-weight: 600;
  color: #ffffff;
  line-height: 1;
  transition: transform 200ms ease;

  /* Counter the parent's scale transform to keep the indicator stable */
  ${MicButton}:hover:not(:disabled) & {
    transform: scale(0.952); /* 1 / 1.05 ≈ 0.952 to maintain original size */
  }

  /* Counter the pulse animation scaling during recording */
  ${MicButton}[data-listening="true"] & {
    animation: counterPulse 1.5s infinite;
  }

  @keyframes counterPulse {
    0% {
      transform: scale(1);
    }
    50% {
      transform: scale(0.952); /* Counter the 1.05 scale from pulse */
    }
    100% {
      transform: scale(1);
    }
  }

  @media (max-width: 768px) {
    width: 16px;
    height: 16px;
    font-size: 10px;
    top: -1px;
    right: -1px;
  }
`;

const DropdownArrowButton = styled.button<{
  $theme: any;
  disabled: boolean;
  isOpen: boolean;
}>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  background: none;
  border: none;
  color: ${(props) => props.$theme.colors.mainText};
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
  opacity: ${(props) => (props.disabled ? 0.5 : 1)};
  transition: all 200ms ease;
  padding: 0;
  &:hover:not(:disabled) {
    opacity: 0.7;
  }

  svg {
    width: 12px;
    height: 12px;
    transition: transform 200ms ease;
    transform: ${(props) => (props.isOpen ? 'rotate(180deg)' : 'rotate(0deg)')};
  }

  @media (max-width: 768px) {
    width: 18px;
    height: 18px;

    svg {
      width: 10px;
      height: 10px;
    }
  }
`;

const DropdownMenu = styled.div<{ $theme: any }>`
  position: absolute;
  bottom: calc(100% + 8px);
  right: 0;
  background: ${(props) => props.$theme.colors.messageBoxBg};
  border: 1px solid ${(props) => props.$theme.colors.messageBoxBorder};
  border-radius: 12px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  width: 350px;
  max-height: 400px;
  overflow: hidden;
  animation: slideUp 200ms ease-out;

  @keyframes slideUp {
    from {
      opacity: 0;
      transform: translateY(8px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @media (max-width: 768px) {
    width: 320px;
    max-height: 350px;
    right: auto;
    left: -240px;
  }

  @media (max-width: 480px) {
    width: 300px;
    max-height: 300px;
    left: -220px;
  }
`;

const LanguagesList = styled.div`
  overflow-y: auto;
  max-height: 400px;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.2);
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: rgba(0, 0, 0, 0.3);
  }

  @media (max-width: 768px) {
    max-height: 350px;
  }

  @media (max-width: 480px) {
    max-height: 300px;
  }
`;

const CountryGroup = styled.div`
  &:not(:last-child) {
    margin-bottom: 4px;
  }
`;

const CountryHeader = styled.div<{ $theme: any; themeMode: string }>`
  padding: 10px 14px;
  font-size: 13px;
  font-weight: 600;
  color: ${(props) => props.$theme.colors.mainText};
  opacity: 1;
  background: ${(props) => (props.themeMode == 'light' ? 'rgb(237, 231, 226)' : 'rgb(89, 89, 89)')};
  border-bottom: 1px solid ${(props) => props.$theme.colors.messageBoxBorder};
  position: sticky;
  top: 0;
  z-index: 1;

  @media (max-width: 768px) {
    padding: 8px 12px;
    font-size: 12px;
  }

  @media (max-width: 480px) {
    padding: 8px 10px;
    font-size: 11px;
  }
`;

const LanguagesGroup = styled.div`
  background: ${(props) => props.theme?.colors?.messageBoxBg || 'transparent'};
`;

const LanguageOption = styled.div<{
  $theme: any;
  isSelected: boolean;
}>`
  padding: 10px 14px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  transition: all 150ms ease;
  background: ${(props) => (props.isSelected ? '#2563eb15' : 'transparent')};

  &:hover {
    background: ${(props) =>
      props.isSelected ? '#2563eb20' : props.$theme.colors.messageBoxBorder}30;
  }

  @media (max-width: 768px) {
    padding: 8px 12px;
  }

  @media (max-width: 480px) {
    padding: 8px 10px;
  }
`;

const LanguageInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
`;

const LanguageFlag = styled.span`
  font-size: 16px;
  width: 20px;
  text-align: center;
`;

const LanguageName = styled.span<{ $theme?: any }>`
  font-size: 14px;
  font-weight: 500;
  color: ${(props) => props.$theme?.colors?.mainText || 'inherit'};

  @media (max-width: 768px) {
    font-size: 13px;
  }

  @media (max-width: 480px) {
    font-size: 12px;
  }
`;

const LanguageCode = styled.span<{ $theme?: any }>`
  font-size: 12px;
  opacity: 0.6;
  color: ${(props) => props.$theme?.colors?.mainText || 'inherit'};
  font-family: monospace;

  @media (max-width: 768px) {
    font-size: 11px;
  }

  @media (max-width: 480px) {
    font-size: 10px;
  }
`;

export default VoiceInputButton;
